import React, { createContext, useContext, useState, useMemo, useEffect, useCallback } from 'react'
import { UserContext } from './userContext'
import { getStoreNameById, getStoreOptions } from '../utils/storeMapping'
import { clearAllUserData, emergencyCleanup } from '../api/authApiService'

export const StoreContext = createContext(null)

const StoreProvider = ({ children }) => {
  const {
    cognitoData,
    userAccessData, // Get userAccessData from UserContext
    logoutUser // Get logout function from UserContext
  } = useContext(UserContext) || {}

  const loginType = localStorage.getItem('loginType')

  // State to track the selected store (used for AD users when switching stores)
  const [selectedStore, setSelectedStoreState] = useState(() => {
    try {
      const savedStore = localStorage.getItem('selectedStore')
      return savedStore ? JSON.parse(savedStore) : null
    } catch {
      return null
    }
  })

  const getUserAccessData = () => {
    // First try to get from UserContext (most up-to-date)
    if (userAccessData) {
      return userAccessData
    }

    // Fallback to localStorage
    try {
      const userAccessDataFromStorage = localStorage.getItem('userAccessData')
      return userAccessDataFromStorage ? JSON.parse(userAccessDataFromStorage) : null
    } catch {
      return null
    }
  }

  // Get user stores from API response (works for both Cognito and SSO users)
  const getUserStoresFromApi = () => {
    const accessData = getUserAccessData()
    return accessData?.message?.stores || []
  }

  // Enhanced clear all caches - centralized function with comprehensive cleanup
  const clearApplicationCache = useCallback(() => {
    console.log('Clearing application cache...')

    const itemsToClear = [
      'petty_cash_transactions',
      'cash_counts',
      'daily_reports',
      'user_preferences',
      'api_cache',
      'shelf_edge_data',
      'requested_items_data',
      'userAccessData',
      'userAccessDataTimestamp',
      'selectedStore',
      'tokenData',
      'msalAccessToken',
      'authenticated',
      'loginType'
    ]

    // Clear from both localStorage and sessionStorage
    itemsToClear.forEach(item => {
      try {
        localStorage.removeItem(item)
        sessionStorage.removeItem(item)
      } catch (error) {
        console.warn(`Error removing ${item}:`, error)
      }
    })

    // Clear service worker caches if applicable
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => {
          caches.delete(name)
        })
      }).catch(error => {
        console.warn('Error clearing service worker caches:', error)
      })
    }

    console.log('Application cache cleared')
  }, [])

  // Function to clear store-specific data
  const clearStoreData = useCallback(() => {
    console.log('Clearing store context data...')

    setSelectedStoreState(null)
    window.currentStoreId = null

    try {
      localStorage.removeItem('selectedStore')
    } catch (error) {
      console.warn('Error clearing store data:', error)
    }

    console.log('Store context data cleared')
  }, [])

  // Enhanced logout function with comprehensive cleanup
  const logout = useCallback(async () => {
    try {
      console.log('Starting comprehensive logout process...')

      // Clear user context first
      if (logoutUser) {
        logoutUser()
      }

      // Clear store context data
      clearStoreData()

      // Clear all application caches
      clearApplicationCache()

      // Use the comprehensive cleanup from authApiService
      clearAllUserData()

      console.log('Logout process completed successfully')

      // Force a complete page reload to clear any remaining state
      window.location.href = '/login'

    } catch (error) {
      console.error('Error during logout process:', error)

      // Fallback to emergency cleanup
      try {
        emergencyCleanup()
        window.location.href = '/login'
      } catch (emergencyError) {
        console.error('Emergency cleanup also failed:', emergencyError)
        // Last resort - force navigation
        window.location.href = '/login'
      }
    }
  }, [logoutUser, clearStoreData, clearApplicationCache])

  // Determine store information based on API response for both login types
  const storeInfo = useMemo(() => {
    let storeData = {
      name: null,
      code: null,
      id: null,
      isAuthenticated: false
    }

    // Get stores from API response (works for both Cognito and Microsoft users)
    const userStoresFromApi = getUserStoresFromApi()

    if (userStoresFromApi && userStoresFromApi.length > 0) {
      let targetStore = null

      // For Cognito users - use the first (and likely only) store from API
      if (loginType === 'cognito') {
        const firstStoreId = userStoresFromApi[0]
        targetStore = {
          name: getStoreNameById(firstStoreId),
          id: firstStoreId
        }
      }

      // For Microsoft AD users - handle multiple stores with selection logic
      if (loginType === 'microsoft') {
        // Check if selected store is still accessible
        if (selectedStore && selectedStore.id) {
          const isStoreAccessible = userStoresFromApi.includes(selectedStore.id)

          if (isStoreAccessible) {
            targetStore = {
              name: getStoreNameById(selectedStore.id),
              id: selectedStore.id
            }
          } else {
            // Clear invalid selected store
            setSelectedStoreState(null)
            localStorage.removeItem('selectedStore')
          }
        }

        // If no valid selected store, default to the first accessible store
        if (!targetStore) {
          const firstStoreId = userStoresFromApi[0]
          targetStore = {
            name: getStoreNameById(firstStoreId),
            id: firstStoreId
          }

          // Update selected store to the first accessible one
          setSelectedStoreState(targetStore)
        }
      }

      if (targetStore) {
        storeData = {
          name: targetStore.name,
          id: targetStore.id,
          code: targetStore.id, // For backward compatibility
          isAuthenticated: true
        }
      }
    } else {
      // No store access from API
      storeData = {
        name: null,
        id: null,
        code: null,
        isAuthenticated: loginType ? true : false
      }
    }

    // Set a global variable for API calls
    if (storeData.id) {
      window.currentStoreId = storeData.id
    }

    return storeData
  }, [loginType, cognitoData, selectedStore, userAccessData]) // Added userAccessData dependency

  // Persist store info to localStorage when it changes
  useEffect(() => {
    if (storeInfo && storeInfo.id) {
      try {
        localStorage.setItem('selectedStore', JSON.stringify({
          name: storeInfo.name,
          id: storeInfo.id
        }))
      } catch (error) {
        console.warn('Error persisting store info:', error)
      }
    }
  }, [storeInfo])

  const setStore = (newStore, shouldRefresh = false) => {
    // Validate that the new store is in the user's accessible stores
    const userStoresFromApi = getUserStoresFromApi()
    const isValidStore = userStoresFromApi.includes(newStore.id)

    if (!isValidStore) {
      console.warn('Attempted to set store that user does not have access to:', newStore.id)
      return
    }

    // Ensure we have proper store data with mapped name
    const storeWithProperData = {
      name: newStore.name || getStoreNameById(newStore.id),
      id: newStore.id
    }

    // Update state
    setSelectedStoreState(storeWithProperData)

    // Persist to localStorage
    try {
      localStorage.setItem('selectedStore', JSON.stringify(storeWithProperData))
    } catch (error) {
      console.warn('Error persisting new store selection:', error)
    }

    // Update the global store ID for API calls
    window.currentStoreId = storeWithProperData.id

    // Refresh the page if requested to apply changes globally
    if (shouldRefresh) {
      // Clear cache items before refresh
      const cacheItems = [
        'petty_cash_transactions',
        'cash_counts',
        'daily_reports',
        'user_preferences',
        'api_cache',
        'shelf_edge_data',
        'requested_items_data'
      ]

      cacheItems.forEach(item => {
        sessionStorage.removeItem(item)
        localStorage.removeItem(item)
      })

      // Small delay to ensure localStorage is updated before refresh
      setTimeout(() => {
        window.location.reload()
      }, 100)
    }
  }

  // Reset selected store when user logs out
  useEffect(() => {
    if (!loginType) {
      clearStoreData()
    }
  }, [loginType, clearStoreData])

  // Get available store options for dropdowns
  const getAvailableStores = () => {
    const userStoresFromApi = getUserStoresFromApi()
    return getStoreOptions(userStoresFromApi)
  }

  // Helper function to get current store data safely
  const getCurrentStoreData = () => {
    return {
      id: storeInfo.id || null,
      name: storeInfo.name || '',
      code: storeInfo.code || storeInfo.id || null
    }
  }

  // Get user stores for other components
  const getUserStores = () => {
    return getUserStoresFromApi()
  }

  // Function to validate current store access
  const validateStoreAccess = useCallback(() => {
    const userStoresFromApi = getUserStoresFromApi()

    if (storeInfo.id && !userStoresFromApi.includes(storeInfo.id)) {
      console.warn('Current store is no longer accessible, clearing...')
      clearStoreData()
      return false
    }

    return true
  }, [storeInfo.id, getUserStoresFromApi, clearStoreData])

  // The value provided to consumers includes the store info and setter
  const contextValue = {
    // Ensure all properties have safe defaults
    name: storeInfo.name || '',
    id: storeInfo.id || null,
    code: storeInfo.code || storeInfo.id || null,
    isAuthenticated: storeInfo.isAuthenticated || false,

    // Functions - Enhanced with logout support
    setStore,
    logout, // Enhanced logout function
    clearCache: clearApplicationCache,
    clearStoreData, // New function to clear just store data
    getAvailableStores,
    getCurrentStoreData,
    getUserStores,
    validateStoreAccess, // New function to validate current store

    // Add some helper properties
    hasMultipleStores: getUserStoresFromApi().length > 1, // Updated to use API response
    isStoreLoaded: !!storeInfo.name,

    // Backward compatibility properties
    storeName: storeInfo.name || '',
    storeId: storeInfo.id || null,
    storeCode: storeInfo.code || storeInfo.id || null
  }

  return (
    <StoreContext.Provider value={contextValue}>
      {children}
    </StoreContext.Provider>
  )
}

// Custom hook for using the store context
export default StoreProvider